/**
 * ServiceTypesTab — iter251
 * INVOICES grouped by service type over a date range (quotes excluded).
 * Types derive from the invoice's own line items (mapped onto the job-card
 * service-type vocabulary, with linked-JC fallback). Revenue = invoice
 * totals. Data: GET /api/reports/service-types.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Wrench, X, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtCurrency = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n || 0);

export default function ServiceTypesTab({ getSiteParams }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [serviceType, setServiceType] = useState('all');
  const [page, setPage] = useState(1);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ ...getSiteParams(), page: String(page), page_size: '50' });
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (serviceType && serviceType !== 'all') params.append('service_type', serviceType);
      const res = await fetch(`${BACKEND_URL}/api/reports/service-types?${params}`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
      else toast.error('Failed to load service type report');
    } catch (_e) {
      toast.error('Failed to load service type report');
    } finally {
      setLoading(false);
    }
  }, [getSiteParams, dateFrom, dateTo, serviceType, page]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const hasFilters = dateFrom || dateTo || (serviceType && serviceType !== 'all');
  const summary = data?.summary || [];
  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="service-types-tab">
      {/* Filters */}
      <Card className="p-4 bg-slate-50">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-700 flex items-center gap-2">
            <Calendar className="h-4 w-4" /> Invoice Date &amp; Service Type
          </h3>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); setServiceType('all'); setPage(1); }} data-testid="st-clear-filters">
              <X className="h-4 w-4 mr-1" /> Clear All
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex gap-2">
            <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="flex-1" data-testid="st-date-from" />
            <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="flex-1" data-testid="st-date-to" />
          </div>
          <Select value={serviceType} onValueChange={(v) => { setServiceType(v); setPage(1); }}>
            <SelectTrigger data-testid="st-type-select">
              <SelectValue placeholder="All service types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All service types</SelectItem>
              {summary.map((s) => (
                <SelectItem key={s.service_type} value={s.service_type} data-testid={`st-type-option-${s.service_type}`}>
                  {s.service_type} ({s.invoices})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Totals strip */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500">Invoices in Range</p>
          <p className="text-2xl font-bold text-slate-900" data-testid="st-total-jobs">{(data?.total_invoices ?? 0).toLocaleString()}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500">Invoice Revenue</p>
          <p className="text-2xl font-bold text-emerald-700" data-testid="st-total-revenue">{fmtCurrency(data?.total_revenue)}</p>
        </Card>
      </div>

      {/* Summary by service type */}
      <Card className="overflow-hidden">
        <div className="px-4 py-3 border-b bg-slate-50 flex items-center gap-2">
          <Wrench className="h-4 w-4 text-slate-500" />
          <h3 className="font-semibold text-slate-700">Invoices by Service Type</h3>
          <span className="text-[11px] text-slate-400 ml-2">an invoice with two types counts in both rows</span>
        </div>
        <div className="overflow-x-auto max-h-72 overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Service Type</TableHead>
                <TableHead className="font-semibold text-right">Invoices</TableHead>
                <TableHead className="font-semibold text-right">Revenue</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summary.length === 0 ? (
                <TableRow><TableCell colSpan={3} className="text-center py-8 text-slate-500">No invoices in this range</TableCell></TableRow>
              ) : summary.map((s) => (
                <TableRow
                  key={s.service_type}
                  className="cursor-pointer hover:bg-blue-50"
                  onClick={() => { setServiceType(s.service_type); setPage(1); }}
                  data-testid={`st-summary-row-${s.service_type}`}
                >
                  <TableCell className="font-medium">{s.service_type}</TableCell>
                  <TableCell className="text-right">{s.invoices}</TableCell>
                  <TableCell className="text-right font-semibold">{fmtCurrency(s.revenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Detail listing */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Date</TableHead>
                <TableHead className="font-semibold">Invoice #</TableHead>
                <TableHead className="font-semibold">Customer</TableHead>
                <TableHead className="font-semibold">Rego</TableHead>
                <TableHead className="font-semibold">Service Types</TableHead>
                <TableHead className="font-semibold text-center">Status</TableHead>
                <TableHead className="font-semibold text-right">Invoice Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
                </TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-500">
                  <Wrench className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                  No invoices found for this filter
                </TableCell></TableRow>
              ) : items.map((r) => (
                <TableRow key={r.invoice_id} className="hover:bg-slate-50">
                  <TableCell>{r.date || '-'}</TableCell>
                  <TableCell>
                    <button
                      onClick={() => navigate(`/invoices/${r.invoice_id}`)}
                      className="text-blue-600 hover:underline font-medium"
                      data-testid={`st-inv-link-${r.invoice_id}`}
                    >
                      {r.doc_no || '-'}
                    </button>
                  </TableCell>
                  <TableCell className="max-w-[160px] truncate" title={r.customer_name}>{r.customer_name || '-'}</TableCell>
                  <TableCell className="font-mono text-sm">{r.vehicle_rego || '-'}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(r.service_types || []).map((t) => (
                        <Badge key={t} variant="secondary" className="bg-blue-50 text-blue-700">{t}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="secondary" className="bg-slate-100 text-slate-700">{r.status || '-'}</Badge>
                  </TableCell>
                  <TableCell className="text-right font-medium">{r.invoice_total ? fmtCurrency(r.invoice_total) : '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
          <span className="text-sm text-slate-600">
            {data?.total ? `${data.total.toLocaleString()} invoices · page ${data.page} of ${data.total_pages}` : 'No records'}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} data-testid="st-prev-page">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(data?.total_pages || 1, p + 1))} disabled={page >= (data?.total_pages || 1)} data-testid="st-next-page">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
